import DocumentViewerModal from "components/DocumentViewer";
import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import OfficialFeedbackCard from "components/OfficialFeedbackCard";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useToast } from "contexts/ToastContext";
import { clamp } from "date-fns";
import { ChangeEvent, useEffect, useState } from "react";
import { updateLegislativeAdditionalInfo, updateLegislativeDrafting } from "services/legislativeService";
import { updateRegisterDirective, updateRegisterDirectiveAdditionalInfo } from "services/registerDirectivesService";
import ShowUI, { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { formatDay } from "utils/helpers";

export default function StakeholderRightPanel({ request }: { request: any }) {
    const contextData = useAuth();
    const { t } = useTranslation();
    const [formState, setFormState] = useState({ ...request });
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const [edit, setEdit] = useState(
        formState?.status !== LEGISLATIVE_DRAFTING_REQUEST_STATUS.waiting_for_additional_info.value &&
        formState?.status !== "additional_info_requested"
    );
    const handleChange = (
        e:
            | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({ ...prev, [name]: value }));
    };
    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "response_file"
        >
    ) => {
        setFormState((prev: any) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleForward = async (froward_status: string) => {
        const data = new FormData();

        data.append(
            "forward_status",
            froward_status
        );

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast("Register Directives Forwarded successfully!", "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast("Failed to update Register Directives request.", "error");
        } finally {
            setLoading(false);
        }
    };
    const handleAudit = async () => {
        setLoading(true);
        const data = new FormData();
        if (formState.response_file)
            data.append("response_files", formState.response_file);
        data.append("response_note", formState.additional_info_summary);
        data.append("responded_by_id", formState.stack_holder_id);

        try {
            const additionalInfoResponse = await updateRegisterDirectiveAdditionalInfo(contextData.token || contextData?.user?.access_token || "", formState.additional_infos[0].id, data);
            const draftData = new FormData();
            draftData.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.additional_info_submitted.value);
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, draftData);

            setFormState((prev: any) => ({
                ...prev,
                ...response,
                additional_infos: prev.additional_infos?.map((info: any, idx: number) =>
                    idx === 0 ? { ...info, ...additionalInfoResponse } : info
                ),
            }));
            showToast("Register Directives Additional Info Submited successfully!", "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast("Failed to update Register Directives request.", "error");
        } finally {
            setLoading(false);
            setEdit(true);
        }
    };
    useEffect(() => { console.log(edit) }, [edit, formState]);
    return (
        <div className="flex flex-col w-[40%]">
            <OfficialFeedbackCard feedbacks={formState.respond_back_feedbacks} />
            {!!formState?.additional_infos?.[0] && (
                <div className="h-fit overflow-y-scroll scrollbar-hide flex flex-col bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-gray-200 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <path d="M24 9.35869C24 10.4661 23.032 11.413 21.9 11.413H17.2195C17.9854 12.2122 18.0795 13.7458 16.9242 14.7385C17.594 15.8086 17.0249 17.2237 16.1531 17.6595C16.5738 19.963 15.1244 21 12.75 21C12.6213 21 12.1277 20.9905 12 20.9909C9.09511 20.9987 8.39559 19.5347 6.20011 19.1948C5.65294 19.1101 5.25 18.6375 5.25 18.0837V10.0435L5.25009 10.0434C5.25061 9.18253 5.7473 8.36114 6.58434 7.9882C7.93837 7.37911 11.0568 5.68955 11.625 4.36369C11.9905 3.51084 12.6274 3.00038 13.5 3C15.1042 2.99934 16.2071 4.64522 15.5681 6.13631C15.4001 6.52814 15.1785 6.91772 14.9047 7.30434H21.9C22.9992 7.30434 24 8.2673 24 9.35869Z" fill="#0A1D39" />
                        </svg>
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#9B0505] font-semibold">{t("OAG") || "OAG"}</p>
                            <p className="text-[#0A1D39] font-semibold text-base">{t("AdditionalInformationRequest")}</p>
                        </div>
                    </div>
                    <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos?.[0]?.requested_at).rest}</p>
                    <p className="font-regular text-base text-[#606060] text-justify">{formState?.additional_infos?.[0]?.request_summary}</p>
                    {formState?.additional_infos?.[0]?.documents?.find((document: any) => document.document_type === "reg_dir_additional_request_file") &&
                        <div className="mt-4">
                            <DocumentViewerModal
                                documents={formState?.additional_infos?.[0]?.documents.filter((document: any) => document.document_type === "reg_dir_additional_request_file")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}//api/accuracy_government_sector/directive-respond-back-feedback-files/document`}
                            />
                        </div>
                    }
                </div>
            )}
            {/* Research Completion Letter - visible to external users */}
            {formState?.documents?.find((document: any) => document.document_type === "dir_reg_research_completion_letter") && (
                <div className="h-fit overflow-y-scroll scrollbar-hide flex flex-col bg-white rounded-lg shadow-md mt-4 p-6 gap-4">
                    <div className="pb-2 border-b border-gray-200 mb-2">
                        <p className="font-semibold text-gray-800">{t("CompletionLetter")}</p>
                    </div>
                    <DocumentViewerModal
                        documents={[formState?.documents?.find((document: any) => document.document_type === "dir_reg_research_completion_letter")]}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                    />
                </div>
            )}
            {edit &&
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_stakeholder.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <div className="h-fit overflow-y-scroll scrollbar-hide flex flex-col bg-white rounded-lg shadow-md mt-8 p-6 gap-4">
                        <div className="flex">
                            <div className="pb-2">
                                <p>{t("AdditionalInformationResponse")}</p>
                            </div>
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_prosecutor.value}
                                currentStatus={formState.status}
                                forwardStatus={formState.forward_status}
                            >
                                <div className="flex mb-auto ml-auto gap-4">
                                    <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                        </svg>
                                    </div>
                                    <div className="flex gap-4">
                                        <button onClick={() => handleForward(LEGISLATIVE_DRAFTING_REQUEST_STATUS.assigned_to_prosecutor.value)} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                                    </div>
                                </div>
                            </ShowUI>
                        </div>
                        <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos?.[0]?.responded_at).rest}</p>
                        <p className="font-regular w-[55ch] text-justify text-base text-[#606060]">{formState?.additional_infos?.[0]?.response_note}</p>
                        {formState?.additional_infos[0]?.documents?.find((document: any) => document.document_type === "reg_dir_additional_response_file") &&
                            <DocumentViewerModal
                                documents={formState?.additional_infos[0]?.documents.filter((document: any) => document.document_type === "reg_dir_additional_response_file")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}/api/directive_registration/directive-additional-info-documents/document`}
                            />
                        }
                    </div>
                </ShowUI>
            }
            {!edit &&
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_additional_info.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <div className="h-fit overflow-y-scroll scrollbar-hide flex flex-col bg-white rounded-lg shadow-md mt-8 p-6 gap-4">
                        <div className="mb-4">
                            <DynamicFormInput
                                inputs={[
                                    {
                                        value: formState.response_note,
                                        type: "textarea",
                                        label: t("AdditionalInfoSummary"),
                                        name: "additional_info_summary",
                                        onChange: handleChange,
                                    },
                                    {
                                        // No 'value' prop for file inputs
                                        type: "file",
                                        label: t("AdditionalInfoDoc"),
                                        name: "response_file",
                                        onChange: (file) => handleFileChange(file, "response_file"),
                                    },
                                ]}
                            />
                        </div>
                        <div className="flex">
                            <button
                                onClick={handleAudit}
                                disabled={loading}
                                className="flex-1 flex items-center justify-center gap-2 border border-black p-4 rounded-lg bg-[#0A1D39] disabled:opacity-70 text-white font-semibold text-base"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t("Submitting") || "Submitting..."}
                                    </>
                                ) : (
                                    t("Submit")
                                )}
                            </button>
                        </div>
                    </div>
                </ShowUI>
            }
        </div>
    );
}
